// src/pages/DegenPoetryDetailPage.js

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import QRCode from 'react-qr-code';
import { useWallet } from '@solana/wallet-adapter-react';
import { createNft, mplTokenMetadata } from '@metaplex-foundation/mpl-token-metadata';
import { mplCore } from '@metaplex-foundation/mpl-core';
import {
    signerIdentity,
    generateSigner,
    percentAmount,
    createGenericFile,
} from '@metaplex-foundation/umi';
import { createUmi } from '@metaplex-foundation/umi-bundle-defaults';
import { bundlrUploader } from '@metaplex-foundation/umi-uploader-bundlr';
import { walletAdapterIdentity } from '@metaplex-foundation/umi-signer-wallet-adapters';

const MAX_DOWNLOADS_PER_DAY = 3;

const DegenPoetryDetailPage = () => {
    const { id } = useParams();
    const wallet = useWallet();
    const [poemData, setPoemData] = useState(null);
    const [downloadCount, setDownloadCount] = useState(0);
    const [status, setStatus] = useState('');

    useEffect(() => {
        const fetchPoem = async () => {
            const docRef = doc(db, 'poem_spins', id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setPoemData(docSnap.data());
            }
        };

        fetchPoem();

        const today = new Date().toISOString().split('T')[0];
        const key = `poem_downloads_${id}_${today}`;
        const count = parseInt(localStorage.getItem(key) || '0', 10);
        setDownloadCount(count);
    }, [id]);

    const handleDownload = () => {
        if (!poemData?.imageUrl) return;

        const today = new Date().toISOString().split('T')[0];
        const key = `poem_downloads_${id}_${today}`;
        let count = parseInt(localStorage.getItem(key) || '0', 10);

        if (count >= MAX_DOWNLOADS_PER_DAY) {
            alert('Download limit reached for today.');
            return;
        }

        const link = document.createElement('a');
        link.href = poemData.imageUrl;
        link.download = `degen_poem_${id}.png`;
        link.click();

        count += 1;
        localStorage.setItem(key, count.toString());
        setDownloadCount(count);
    };

    const handleMint = async () => {
        if (!wallet.connected || !poemData?.imageUrl) {
            alert("Connect wallet first.");
            return;
        }

        try {
            setStatus("Uploading & minting…");
            const response = await fetch(poemData.imageUrl, { mode: "cors" });
            const blob = await response.blob();
            if (!blob) throw new Error("Failed to fetch image blob");

            const ts = new Date().toISOString().replace(/[-:.TZ]/g, "").slice(0, 13);
            const fname = `DegenPoem-${ts}.png`;

            const umi = createUmi(process.env.REACT_APP_RPC_ENDPOINT)
                .use(signerIdentity(wallet))
                .use(mplCore())
                .use(mplTokenMetadata())
                .use(
                    bundlrUploader("https://node1.bundlr.network", {
                        identity: signerIdentity(wallet),
                    })
                )
                .use(walletAdapterIdentity(wallet));

            const arrayBuffer = await blob.arrayBuffer();
            const imageFile = createGenericFile(arrayBuffer, fname, {
                tags: [{ name: "Content-Type", value: "image/png" }],
            });
            const arweaveUri = (await umi.uploader.upload([imageFile]))[0];

            const metadata = {
                name: fname.slice(0, 32),
                description: "A generative poem minted from Shlot Machines pull.",
                image: arweaveUri,
                attributes: [
                    { trait_type: "Score", value: poemData.totalScore },
                    { trait_type: "Match Result", value: poemData.matchResult },
                ],
                properties: { files: [{ uri: arweaveUri, type: "image/png" }], category: "image" },
            };

            setStatus("Uploading metadata…");
            const metadataUri = await umi.uploader.uploadJson(metadata);

            setStatus("Sending mint transaction…");
            const mint = generateSigner(umi);
            await createNft(umi, {
                mint,
                name: metadata.name,
                uri: metadataUri,
                sellerFeeBasisPoints: percentAmount(5.5),
            }).sendAndConfirm(umi);

            setStatus("🎉 Minted! 🎉");
        } catch (err) {
            console.error(err);
            setStatus(`Error: ${err.message}`);
        }
    };

    if (!poemData) return <p>Loading poem...</p>;

    return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
            <h2>Your Degenerate Poem</h2>
            <img src={poemData.imageUrl} alt="Generated Poem" style={{ maxWidth: '100%', borderRadius: '8px' }} />
            <div style={{ marginTop: '1rem' }}>
                <div
                    style={{
                        display: "inline-block",
                        background: "#fff",
                        padding: "1rem",
                        borderRadius: "12px",
                        boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                    }}
                >
                    <QRCode value={window.location.href} />
                </div>
                <p><strong>Score:</strong> {poemData.totalScore} | <strong>Match Result:</strong> {poemData.matchResult}</p>
                <p><strong>Downloads left today:</strong> {MAX_DOWNLOADS_PER_DAY - downloadCount}</p>
                <button onClick={handleDownload} disabled={downloadCount >= MAX_DOWNLOADS_PER_DAY}>Download Image</button>
                <button onClick={handleMint} style={{ marginLeft: '1rem' }}>Mint NFT</button>
                {status && <p>{status}</p>}
            </div>
        </div>
    );
};

export default DegenPoetryDetailPage;
